import { useLocalSearchParams } from 'expo-router';
import { TabList, TabSlot, Tabs, TabTrigger } from 'expo-router/ui';
import { useRef } from 'react';
import { type ScrollView, StyleSheet } from 'react-native';
import { useQuery } from 'urql';
import Nav from '@/components/Custom/Nav';
import * as NavItem from '@/components/Custom/NavItem';
import { graphql, readFragment } from '@/gql/graphql';
import EventDetailToolbar, {
  EventDetailToolbarFragment,
} from './EventDetailToolbar';

const EventDetailNavFragment = graphql(/* GraphQL */ `
  fragment EventDetailNav on event {
    id
    has_standings
    event_type
    permissions
    stat_widget_id
    is_group
    event_segment_group_id
    event {
      id
      has_standings
      stat_widget_id
    }
    event_teams_aggregate {
      aggregate {
        count
      }
    }
    events_aggregate(where: { is_group: { _eq: true } }) {
      aggregate {
        count
      }
    }
  }
`);

const GetEventDetailLayout = graphql(/* GraphQL */ `
  query GetEventDetailLayout($eventId: uuid!) {
    event_by_pk(id: $eventId) {
      id
      status @_optional
      datetime @_optional
      permissions @_optional
      title @_optional
      ...EventDetailToolbar
      ...EventDetailNav @_optional
    }
  }
`, [EventDetailToolbarFragment, EventDetailNavFragment]);

const style = StyleSheet.create({
  tabSlot: {
    flex: 1,
  },
});

export default function EventDetailLayout({
  basePath = '/event',
}: {
  basePath?: string;
}) {
  const { eventId, teamId } = useLocalSearchParams<{
    eventId?: string;
    teamId?: string;
  }>();
  const [{ data }] = useQuery({
    query: GetEventDetailLayout,
    variables: {
      eventId: eventId || '',
    },
    pause: !eventId,
    requestPolicy: 'cache-and-network',
  });

  const navFragment = readFragment(EventDetailNavFragment, data?.event_by_pk);
  const navRef = useRef<ScrollView>(null);

  const tabTriggers = [
    { name: 'home', shouldRender: true, label: 'Home' },
    {
      name: 'stats',
      shouldRender:
        !!navFragment?.stat_widget_id &&
        (navFragment?.event_type === 'group' ||
          (navFragment?.event_type === 'game' &&
            navFragment?.event_teams_aggregate?.aggregate?.count === 2)),
      label: 'Stats',
    },
    {
      name: 'score',
      shouldRender:
        navFragment?.event_type === 'game' &&
        !!navFragment?.event_segment_group_id &&
        navFragment?.event_teams_aggregate?.aggregate?.count === 2,
      label: 'Score',
    },
  ];

  const visibleTabTriggers = tabTriggers.filter(
    (trigger) => trigger.shouldRender,
  );

  if (!data?.event_by_pk) {
    return null;
  }

  return (
    <>
      <EventDetailToolbar query={data.event_by_pk} />
      <Tabs key={`${eventId ?? 'no-event'}:${teamId ?? 'no-team'}`}>
        <Nav
          ref={navRef}
          data={visibleTabTriggers}
          keyExtractor={(item) => item.name}
          renderItem={({ item, index }) => (
            <TabTrigger resetOnFocus name={item.name} asChild>
              <NavItem.Container index={index}>
                <NavItem.Label>{item.label}</NavItem.Label>
              </NavItem.Container>
            </TabTrigger>
          )}
        />
        <TabList>
          <TabTrigger
            name="home"
            href={{
              pathname: `${basePath}/[eventId]`,
              params: { eventId, teamId },
            }}
          />
          <TabTrigger
            name="stats"
            href={{
              pathname: `${basePath}/[eventId]/stats`,
              params: { eventId, teamId },
            }}
          />
          <TabTrigger
            name="score"
            href={{
              pathname: `${basePath}/[eventId]/score`,
              params: { eventId, teamId },
            }}
          />
        </TabList>
        <TabSlot style={style.tabSlot} />
      </Tabs>
    </>
  );
}
